/**
 * 2-Level Permission System
 *
 * Level 1: Role-based permissions (user's role determines base access)
 * Level 2: Folder security — Group Security v3 ADDITIVE-OR resolver (spec §5). Access is the UNION
 *          of matched grants across securities[{userId,isEdit}] / securityFolderTeams[{teamId,isEdit}]
 *          (teamId '-2' = Everyone-in-account) / securityGroupIds[{groupId,isEdit}]:
 *          canView = ANY layer matches, canEdit = OR of the matched entries' isEdit. A folder with no
 *          grants at all is OPEN (default-visible to its owning team). This mirrors the backend
 *          `resolveAccess`/`isOpenFolder` (open-api `shared/access-resolver.ts`) — the single source
 *          of truth — so the FE never shows an action the backend would reject.
 *          §4 ROLE CAP: a GROUP grant's edit half is `isEdit && the group's role in that folder's
 *          teamspace allows edit` (`me.groupCapabilities` → `isGroupEditCapable`). Fail closed when
 *          the cap is unknown. The cap is per group; the OR-merge across groups happens after it.
 *
 * See: docs/subsystems/ted/fundamentals/folder-security.md
 */
import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useRootActionLock } from '@/contexts/RootActionLockContext';
import { isGroupEditCapable } from '@/lib/folder-security';
import {
  ROLE_TYPES,
  FOLDER_SECURITY_TYPES,
  type RoleType,
  type MeResponse,
  type BaseFolderSecurity,
  type CompanyInfo,
  type GroupEditCapability,
  isCustomRoleId,
} from '@scio3/shared-types';

// Root-level admin roles (for isRootAdmin — rootRole is never TEAM_OWNER)
const ROOT_ADMIN_ROLES: RoleType[] = [ROLE_TYPES.ADMIN, ROLE_TYPES.SUPER_ADMIN, ROLE_TYPES.OWNER];

/**
 * Roles barred from the content-authoring surfaces — the SINGLE definition.
 *
 * This existed twice and the copies drifted: `RestrictedRoute` included BILLING (deliberately — GS v3
 * D4 makes Billing read-only on content) while `TopNavigation` did not, so a Billing member was shown
 * a Templates nav entry and redirected the instant they clicked it. Both now import this.
 *
 * Adding a role here hides its affordance AND closes its route in one edit, which is the property the
 * two copies could not offer. Asserted by `um-nav-permissions.spec.ts` (UM-NAV-*) and
 * `um-route-guards.spec.ts` (UM-ROUTE-*).
 */
export const CONTENT_RESTRICTED_ROLES: RoleType[] = [
  ROLE_TYPES.READ_ONLY,
  ROLE_TYPES.CONTENT_PROPOSAL,
  ROLE_TYPES.CONTENT_APPROVAL,
  ROLE_TYPES.BILLING,
];

/** Is this role barred from the content-authoring surfaces? See {@link CONTENT_RESTRICTED_ROLES}. */
export function isContentRestrictedRole(role: string | null | undefined): boolean {
  return !!role && CONTENT_RESTRICTED_ROLES.includes(role as RoleType);
}

// Team-level admin roles (includes TEAM_OWNER for isTeamAdmin/isAdminOnTeam)
const ADMIN_ROLES: RoleType[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

// Standard roles that can have security fully disabled
const STANDARD_ROLES: RoleType[] = [
  ROLE_TYPES.OWNER,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.USER,
];

type Entity = 'screen' | 'asset' | 'playlist' | 'schedule';

interface FolderContext {
  folder?: BaseFolderSecurity | null;
  /** For items inside folders, we need the parent folder's security settings */
  parentFolder?: BaseFolderSecurity | null;
}

interface PermissionOptions {
  /**
   * Account payment has lapsed (`authStore.isAccountInactive`). Forces every write
   * permission to false so the UI stops offering actions the backend will refuse:
   * `API_IGNORE_INACTIVE_ACCOUNT` in open-api's `get-auth-context.decorator.ts` allows a
   * lapsed account to READ its content but rejects every content mutation. Without this the
   * paywalled portal would show Add Screen, New Folder and row menus that all fail.
   *
   * Role and admin flags are deliberately left untouched — the trimmed settings nav still
   * gates on `isRootAdmin` to decide whether Subscription Plan and Payment Settings show.
   */
  isAccountInactive?: boolean;
}

interface FolderAccessResult {
  canView: boolean;
  canEdit: boolean;
}

/**
 * A folder is OPEN (default-visible — any same-team member gets view AND edit) ONLY when it carries
 * NO grants at all: securities, securityFolderTeams AND securityGroupIds are all empty. If ANY of the
 * three grant arrays is non-empty the folder is RESTRICTED → deny-by-default (access flows only
 * through a matching grant). Mirror of the backend `isOpenFolder` (open-api `access-resolver.ts`);
 * restricting via securityFolderTeams / securityGroupIds alone MUST NOT read as open.
 */
function isOpenFolder(folder: BaseFolderSecurity): boolean {
  return (
    !(folder.securities && folder.securities.length) &&
    !(folder.securityFolderTeams && folder.securityFolderTeams.length) &&
    !(folder.securityGroupIds && folder.securityGroupIds.length)
  );
}

/**
 * The additive-OR evaluator (spec §5) — mirror of the backend `resolveAccess`. Unions the matched
 * grants across the three layers: securities (userId), securityFolderTeams (teamId, incl the
 * Everyone-in-account sentinel '-2'), securityGroupIds (groupId). canView = any layer matches;
 * canEdit = OR of the matched entries' isEdit. Only-Admin ('-1' in securities) is a write-time
 * restrict — it never equals a real userId, so it simply fails to grant here (non-admins denied).
 *
 * §4 ROLE CAP (`groupCaps`): a GROUP grant confers edit only when `isEdit && isGroupEditCapable(...)`
 * — the group's role in the folder's teamspace must itself allow edit. The cap is applied per group
 * BEFORE the OR-merge, so Viewer-group(isEdit:true) + Editor-group(isEdit:true) still resolves to
 * edit, while a Viewer-role group alone is view-only. Fail closed when the cap is unknown.
 */
/**
 * Teamspaces this user ADMINS — the sixth argument `resolveFolderAccess` needs for Rule 4.
 *
 * Exists because that argument is OPTIONAL and defaults to none, so a caller that omits it fails
 * CLOSED with no type error and no warning. Five pages omitted it and re-implemented the folder
 * check with four arguments, which denied an account OWNER every cross-teamspace folder with no
 * explicit grant — Rule 4 ("a teamspace admin has full view+edit on every folder in that teamspace
 * regardless of its grants") could not fire, so the check fell through to the grant loops and found
 * nothing. The server listed the folder; the client refused to open it (scio3#2416).
 *
 * Derived from `me.teams` and its roles, NOT from the team store: the store's `teams` carry no role,
 * so `useMyTeamIds()` cannot answer this question. Same derivation as `principalAdminTeamIds` below.
 */
export function useMyAdminTeamIds(): string[] {
  const me = useAuthStore((s) => s.me);
  const teams = me?.teams;
  return useMemo(
    () => (teams ?? []).filter((t) => ADMIN_ROLES.includes(t.role as RoleType)).map((t) => t._id),
    [teams],
  );
}

export function resolveFolderAccess(
  folder: BaseFolderSecurity,
  userId: string | undefined,
  teamIds: string[],
  groupIds: string[],
  groupCaps?: readonly GroupEditCapability[] | null,
  /**
   * Teamspaces this user ADMINS — backend Rule 4 / M2: a teamspace admin has full view+edit on every
   * folder in that teamspace regardless of its grants. Optional and defaults to none, so callers that
   * don't know the user's admin scope keep the previous (fail-closed) behavior.
   */
  adminTeamIds?: readonly string[] | null,
): FolderAccessResult {
  const teamSet = new Set<string>([...teamIds, FOLDER_SECURITY_TYPES.EVERYONE_ACCOUNT]);
  const groupSet = new Set<string>(groupIds);
  // EVERYONE_TEAM ('0') in securities = "everyone on the folder's OWNING team" — it grants only when the
  // requester is a member of the folder's own team (raw teamIds, NOT the '-2' account sentinel). Mirrors
  // the backend access-resolver; without this the "Default Team — Everyone on this team" Editor grant is
  // missed and the folder reads as read-only for its own team members.
  const ownsFolderTeam = folder.teamId != null && teamIds.includes(folder.teamId);
  // Rule 4 (M2) — mirror of backend access-resolver.ts:457. Evaluated BEFORE the grant loops because
  // it ignores them entirely: an admin of the folder's teamspace is the highest authority there.
  const adminsFolderTeam = folder.teamId != null && (adminTeamIds ?? []).includes(folder.teamId);
  let canView = false;
  let canEdit = false;
  const grant = (isEdit?: boolean) => {
    canView = true;
    if (isEdit) canEdit = true;
  };
  // Rule 4 (M2): teamspace admin sees + edits everything in a teamspace they admin, any grants.
  if (adminsFolderTeam) grant(true);
  // Rule 3 — TEAMSPACE-VISIBLE. A folder whose `securities` is EMPTY is visible (and editable) to
  // members of its OWN teamspace, regardless of any `securityGroupIds` / `securityFolderTeams` on it.
  // This mirrors the backend resolver (`access-resolver.ts` Rule 3, v4 M1: visibility keys on
  // `securities` ALONE — that is the difference between 10.5% and 96% of folders staying visible).
  //
  // Without this the client denied every OPEN folder in another one of my teamspaces while the server
  // happily served it: All view listed the folder, clicking it bounced to Home with "You do not have
  // access to this folder". The explicit `'0'` marker form of the same rule is handled by the
  // `EVERYONE_TEAM` arm of the loop below, so only the EMPTY case is needed here.
  //
  // The `'-1'` (Only Admin) sentinel is a securities ENTRY, so a folder carrying it is NOT empty and
  // correctly falls through to the grant checks — it must never be caught by this rule.
  if (ownsFolderTeam && (folder.securities ?? []).length === 0) {
    grant(true);
  }
  for (const s of folder.securities ?? []) {
    if (
      s.userId === userId ||
      (s.userId === FOLDER_SECURITY_TYPES.EVERYONE_TEAM && ownsFolderTeam)
    ) {
      grant(s.isEdit);
    }
  }
  for (const t of folder.securityFolderTeams ?? []) {
    if (t.teamId != null && teamSet.has(t.teamId)) grant(t.isEdit);
  }
  for (const g of folder.securityGroupIds ?? []) {
    if (g.groupId == null || !groupSet.has(g.groupId)) continue;
    // §4: intersect the folder-side isEdit flag with the group's ROLE capability in this teamspace.
    // A statement that narrows ⇒ view-only; no statement about this teamspace ⇒ the grant decides (#2173).
    // Only a MISSING cap list (backend never shipped it) fails closed.
    grant(g.isEdit === true && isGroupEditCapable(g.groupId, folder.teamId, groupCaps));
  }
  return { canView, canEdit };
}

// -----------------------------------------------------------------------------
// Level 1: Role-based permissions
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Level 1b: the CAPABILITY plane — Group Security v5 §4e
// -----------------------------------------------------------------------------

/**
 * `can('audit.view')` — the capability question, answered from the ONE list the backend computed
 * (`me.effectivePermissions`, the union of role bundles + group-derived roles + per-user
 * `extraPermissions`; see open-api `shared/effective-permissions.ts`).
 *
 * ─── No rollout flag ───────────────────────────────────────────────────────────────────────────
 * The plane is on for EVERY account (`company.feature.rbacV5` was removed, Ted 2026-08-27). The
 * backend guard admits on `permission OR its declared role list`, so `can()` answering true is always
 * consistent with what the guard will do — and answering false never hides something a role already
 * allows, because the role check is still there beside it:
 *
 *     if (can('audit.view') || isRootAdmin) { ... }     // the union, mirroring the guard
 *
 * That is also why this pass ADDS `can()` beside `isRootAdmin` / `isSuperAdmin` / `isTeamAdmin`
 * instead of replacing them (§4e: "delete the scattered special cases AS surfaces migrate"). Deleting
 * them now would hand every one of the 552 role checks across 92 files to a plane that is off in
 * production.
 *
 * Fails closed on every unknown: no `me`, an un-selected `effectivePermissions` (see the note in
 * `ME_QUERY`), or a key nobody registered ⇒ false. Same contract as the backend `can()`.
 */
export function getCapabilities(me: MeResponse | null) {
  // No rollout flag to consult any more — the only question is whether the backend actually sent the
  // list. An un-selected / missing `effectivePermissions` still fails CLOSED (empty set ⇒ can() is
  // false), so a query that forgot the field cannot silently grant anything.
  const granted = me?.effectivePermissions;
  /**
   * Did the backend actually SEND the permission list? Not a rollout flag any more (there isn't one)
   * — it distinguishes "this user genuinely holds nothing" from "the query forgot to select
   * `effectivePermissions`". Both make `can()` false (fail closed); only the second is a bug.
   */
  const hasPermissionPlane = Array.isArray(granted);
  const permissionSet = new Set<string>(hasPermissionPlane ? granted : []);
  /**
   * The ROOT-scoped plane (scio3#2351). `effectivePermissions` is the union across every teamspace
   * the user reaches, which is the right question for a surface scoped to what they can reach and
   * the WRONG one for an ACCOUNT-WIDE surface: `members.view` lives only in the ADMIN and
   * TEAM_OWNER bundles, so a TEAM_OWNER of one teamspace carried it in the union and the
   * account-wide Users page opened for them — with no active-teamspace input, so switching
   * teamspace never closed it again.
   *
   * Fails closed the same way: a missing field is an empty set.
   */
  const rootPermissionSet = new Set<string>(
    Array.isArray(me?.rootPermissions) ? me.rootPermissions : [],
  );

  /** Holds this permission key. See the doc above for why this is unioned with role checks. */
  function can(permissionKey: string): boolean {
    return permissionSet.has(permissionKey);
  }

  /** Holds ANY of these keys (the guard's `@RequirePermission([a, b])` semantics). */
  function canAny(permissionKeys: string[]): boolean {
    return permissionKeys.some(can);
  }

  /** Holds EVERY one of these keys. An empty list is vacuously true, matching the backend. */
  function canAll(permissionKeys: string[]): boolean {
    if (!permissionKeys.length) return true;
    return permissionKeys.every(can);
  }

  /**
   * Holds this key AT THE ACCOUNT LEVEL — `rootRole`'s bundle plus the Default teamspace's own
   * entry, never a privilege held only in some other teamspace. Use it to gate an ACCOUNT-WIDE
   * surface; use `can` for anything scoped to what the user can reach.
   */
  function canAtRoot(permissionKey: string): boolean {
    return rootPermissionSet.has(permissionKey);
  }

  // NOTE `permissionKeys`, not `permissions` — `currentTeam.permissions` is the legacy 17-boolean
  // map and the two must never be confused at a call site.
  return {
    can,
    canAny,
    canAll,
    canAtRoot,
    hasPermissionPlane,
    permissionKeys: permissionSet,
  };
}

/**
 * Level 1: Role-based permissions (pure function, testable without React).
 * Evaluates RBAC rules based on user's root role and active team role.
 */
export function getRolePermissions(me: MeResponse | null, activeTeamId: string) {
  const currentTeam = me?.teams.find((t) => t._id === activeTeamId);
  const rootRole = me?.rootRole;
  const teamRole = currentTeam?.role;

  const isRootOwner = rootRole === ROLE_TYPES.OWNER;
  const isRootAdmin = ROOT_ADMIN_ROLES.includes(rootRole as RoleType);
  const isSuperAdmin = rootRole === ROLE_TYPES.SUPER_ADMIN;
  const isRootSuperAdmin = isRootOwner || isSuperAdmin;
  const isTeamAdmin = ADMIN_ROLES.includes(teamRole as RoleType);
  // Billing (D4): the Billing role reaches Billing & Orders (payment + ecommerce, gated by
  // @RequireRole(BILLING_ROLES) on the backend) but is content read-only. So the Billing & Orders
  // nav entries show for root admins OR the Billing role — NOT for other below-admin roles.
  const isBillingRole = rootRole === ROLE_TYPES.BILLING;
  const canAccessBilling = isRootAdmin || isBillingRole;

  function canCreate(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // A CUSTOM role (a roleSecurity id) with no permissions = deleted role → deny everything. Was a
    // `length > 1` test, which now also matches the 2-char pre-built codes ('10'/'11') and would deny
    // them on any request where `permissions` had not arrived yet. `isCustomRoleId` is exact.
    if (!perms && isCustomRoleId(teamRole)) return false;
    if (perms) {
      return perms[`${entity}CanCreate`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return entity === 'asset';
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY (its reach is Billing & Orders, not content) — same as
      // Read Only here. The backend enforces this too (ROLE_VALUE_TYPES[BILLING] = all content false).
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  function canEdit(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // A CUSTOM role (a roleSecurity id) with no permissions = deleted role → deny everything. Was a
    // `length > 1` test, which now also matches the 2-char pre-built codes ('10'/'11') and would deny
    // them on any request where `permissions` had not arrived yet. `isCustomRoleId` is exact.
    if (!perms && isCustomRoleId(teamRole)) return false;
    if (perms) {
      return perms[`${entity}CanEdit`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY — same as Read Only. Backend: ROLE_VALUE_TYPES[BILLING].
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  function canDelete(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // A CUSTOM role (a roleSecurity id) with no permissions = deleted role → deny everything. Was a
    // `length > 1` test, which now also matches the 2-char pre-built codes ('10'/'11') and would deny
    // them on any request where `permissions` had not arrived yet. `isCustomRoleId` is exact.
    if (!perms && isCustomRoleId(teamRole)) return false;
    if (perms) {
      return perms[`${entity}CanDelete`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY — same as Read Only. Backend: ROLE_VALUE_TYPES[BILLING].
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  return {
    rootRole,
    teamRole,
    isRootOwner,
    isRootAdmin,
    isSuperAdmin,
    isRootSuperAdmin,
    isTeamAdmin,
    isBillingRole,
    canAccessBilling,
    canCreate,
    canEdit,
    canDelete,
    currentTeam,
    // Group Security v5 §4e — the capability plane, ALONGSIDE the role flags above (not replacing
    // them; surfaces migrate one at a time). `can`/`canAny`/`canAll` + `hasPermissionPlane`.
    ...getCapabilities(me),
  };
}

// -----------------------------------------------------------------------------
// Level 2: Folder-level security
// -----------------------------------------------------------------------------

/**
 * Level 1 + Level 2: Combined permission check (pure function, testable without React).
 * Combines role-based permissions with folder-level security checks.
 */
export function getPermissions(
  me: MeResponse | null,
  company: CompanyInfo | null,
  activeTeamId: string,
  context: FolderContext = {},
  options: PermissionOptions = {},
) {
  const basePermissions = getRolePermissions(me, activeTeamId);
  const isLapsed = options.isAccountInactive === true;
  const { teamRole } = basePermissions;

  const folder = context.folder ?? context.parentFolder;
  const userId = me?._id;

  // Feature flags from company
  const roleSecurity = company?.feature?.roleSecurity ?? true;
  // Folder-security master switch (v3 — the `userGroupSecurity` mode is GONE, D2). When on, folder
  // security is the base and group grants are layered additively (spec §5); no mode derivation.
  const folderLevelSecurity = company?.feature?.folderLevelSecurity ?? false;

  // Check if user is root super admin (bypass all)
  const isRootSuperAdmin = basePermissions.isRootSuperAdmin;

  // Check if user is admin on current team (bypass folder checks on their team)
  const isAdminOnTeam = ADMIN_ROLES.includes(teamRole as RoleType);

  // Check if this is definitively a SAME-team folder.
  // Only true when we KNOW the folder's teamId matches activeTeamId.
  // If teamId is undefined/null (e.g., cache miss or race condition), we treat it
  // as NOT same-team to be conservative and avoid granting unintended access.
  const isSameTeamFolder = folder?.teamId != null && folder.teamId === activeTeamId;

  // Legacy: kept for backwards compatibility in other parts of the return value
  const isCrossTeamFolder = folder?.teamId != null && folder.teamId !== activeTeamId;

  // Check if user has a standard role (can have security disabled)
  const hasStandardRole = STANDARD_ROLES.includes(teamRole as RoleType);

  // Check if roleSecurity is effectively enabled for this user
  const effectiveRoleSecurity = roleSecurity || !hasStandardRole;

  // Check if folderLevelSecurity is effectively enabled for this user
  const effectiveFolderSecurity = folderLevelSecurity || !hasStandardRole;

  // The principal used by the additive-OR resolver. Team reach is EVERY teamspace the user belongs to
  // (`me.teams` — the same server field the team store mirrors), NOT just the active one. The backend
  // authorizes a folder against all of my teamspaces (v4 D2), so `[activeTeamId]` made the client deny
  // folders the server had already served: an OPEN folder in another of my teamspaces failed Rule 3's
  // `ownsFolderTeam` test and read as no-access. `activeTeamId` is unioned in defensively for the
  // window before ME resolves. The Everyone-in-account sentinel is added inside resolveFolderAccess.
  const principalTeamIds = [
    ...new Set([...(me?.teams ?? []).map((t) => t._id), activeTeamId].filter(Boolean)),
  ];
  const principalGroupIds = me?.groupIds ?? [];
  // §4 role cap, computed once by the backend at auth (`me.groupCapabilities`). Undefined ⇒ unknown ⇒
  // group grants are view-only (fail closed) — see isGroupEditCapable.
  const principalGroupCaps = me?.groupCapabilities;
  // Rule 4 (M2) reach: the teamspaces where MY role is an admin role. Backend equivalent:
  // `adminTeamIdsOf(authContext)` (teams whose entry `isAdmin`). Without this a teamspace admin was
  // denied edit on a restricted folder in a teamspace they admin whenever it wasn't the active team.
  const principalAdminTeamIds = (me?.teams ?? [])
    .filter((t) => ADMIN_ROLES.includes(t.role as RoleType))
    .map((t) => t._id);

  /**
   * View + edit capability on ONE folder, applying the same-team OPEN baseline. A folder with no
   * grants (open) is default-visible/editable to its OWNING team only; otherwise access is the §5
   * additive-OR union of the folder's grants. Cross-team access flows solely through securityFolderTeams
   * (incl the Everyone-in-account sentinel), evaluated by resolveFolderAccess.
   */
  function folderCapability(f: BaseFolderSecurity): FolderAccessResult {
    const sameTeam = f.teamId != null && f.teamId === activeTeamId;
    if (sameTeam && isOpenFolder(f)) return { canView: true, canEdit: true };
    return resolveFolderAccess(
      f,
      userId,
      principalTeamIds,
      principalGroupIds,
      principalGroupCaps,
      principalAdminTeamIds,
    );
  }

  /**
   * Whether the user may ACT (create/edit/delete) in the folder context. Group Security v3 (spec §5):
   * the boolean gate maps to canEdit — a viewer-only grant (isEdit:false) reads as no access here so
   * the edit/create/delete affordances hide. Returns true when folder security is bypassed.
   */
  function checkFolderAccess(): boolean {
    // Root Super Admin bypasses all folder checks
    if (isRootSuperAdmin) return true;

    // If no folder context, use role permissions only
    if (!folder) {
      return true;
    }

    // System folders (OnHold) — admins bypass folder checks, non-admins denied
    if (folder.isHoldDevice) return isAdminOnTeam || isRootSuperAdmin;

    // Admin on SAME team bypasses ALL folder security checks. Team admins are the highest authority
    // on their own team — if they can't edit, nobody can. Cross-team admins do NOT get this bypass.
    // Only bypass when we KNOW it's same-team; an undefined folder.teamId conservatively denies it.
    if (isAdminOnTeam && isSameTeamFolder) {
      return true;
    }

    // Master gates: a standard-role user is unrestricted when roleSecurity or folderLevelSecurity is
    // OFF (mirrors the backend — all folder logic, incl. group grants, is gated on folderLevelSecurity).
    if (!effectiveRoleSecurity) return true;
    if (!effectiveFolderSecurity) return true;

    // Group Security v3 — additive-OR (spec §5). Items inherit their folder by path (D3): an item op is
    // gated by context.parentFolder, a folder op by context.folder. The user must be able to VIEW the
    // primary folder, and hold canEdit on each folder present in the context (parent controls items
    // inside it; folder controls the folder itself — both must allow edit, immediate-children only).
    if (!folderCapability(folder).canView) return false;
    if (context.parentFolder && !folderCapability(context.parentFolder).canEdit) return false;
    if (context.folder && !folderCapability(context.folder).canEdit) return false;
    return true;
  }

  const hasFolderAccess = checkFolderAccess();

  /**
   * May the user ACT (edit / delete / move) in ONE arbitrary folder?
   *
   * The list-surface counterpart of `checkFolderAccess()` — same rule chain, but applicable per row so
   * a bulk bar can evaluate every selected folder. Before this, each of the five BulkActionBars
   * hand-reimplemented the chain against the DEPRECATED `folder.isFolderSecurityEdit` field, which
   * defaults to `true` on every folder — so `=== false` almost never matched and those gates had
   * quietly stopped blocking anything (the backend still enforced; the UI just stopped agreeing).
   *
   * Order matters and mirrors `checkFolderAccess`:
   *   1. root super admin      ⇒ allow (bypasses all folder security)
   *   2. OnHold system folder  ⇒ admins only
   *   3. admin of the ACTIVE team, folder on that team ⇒ allow (highest authority on own team)
   *   4. master gates off      ⇒ allow (mirrors the backend, which gates all folder logic on them)
   *   5. otherwise             ⇒ the additive-OR resolver's `canEdit`, incl. Rule 4 teamspace-admin
   */
  function canActInFolder(f: BaseFolderSecurity | null | undefined): boolean {
    if (isRootSuperAdmin) return true;
    if (!f) return true; // no folder context (e.g. a loose item at Root) — role permissions decide
    if (f.isHoldDevice) return isAdminOnTeam || isRootSuperAdmin;
    if (isAdminOnTeam && f.teamId != null && f.teamId === activeTeamId) return true;
    if (!effectiveRoleSecurity) return true;
    if (!effectiveFolderSecurity) return true;
    return folderCapability(f).canEdit;
  }

  // Detect if folder context was provided
  const hasFolderContext = !!(context.folder || context.parentFolder);

  /**
   * Check folder-level permission for edit action.
   */
  function canEditInFolder(entity: Entity): boolean {
    return basePermissions.canEdit(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for delete action.
   */
  function canDeleteInFolder(entity: Entity): boolean {
    return basePermissions.canDelete(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for create action.
   */
  function canCreateInFolder(entity: Entity): boolean {
    return basePermissions.canCreate(entity) && hasFolderAccess;
  }

  // Auto-select folder-aware when context exists, otherwise use role-based.
  // A lapsed account is read-only regardless of role or folder security — see
  // PermissionOptions.isAccountInactive.
  const canCreate = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canCreateInFolder(entity) : basePermissions.canCreate(entity));
  const canEdit = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canEditInFolder(entity) : basePermissions.canEdit(entity));
  const canDelete = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canDeleteInFolder(entity) : basePermissions.canDelete(entity));

  return {
    ...basePermissions,
    canCreate,
    canEdit,
    canDelete,
    hasFolderAccess,
    /**
     * Per-folder edit gate for LIST surfaces (bulk bars, search rows) — evaluate any folder, not just
     * the one in `context`. Replaces every hand-rolled `folder.isFolderSecurityEdit === false` check.
     */
    canActInFolder,
    hasFolderContext,
    /** True when writes are blocked purely because the account's payment lapsed. */
    isReadOnlyLapsedAccount: isLapsed,
    folderLevelSecurity,
    roleSecurity,
    effectiveFolderSecurity,
    effectiveRoleSecurity,
    isCrossTeamFolder: !!isCrossTeamFolder,
  };
}

// -----------------------------------------------------------------------------
// React Hooks
// -----------------------------------------------------------------------------

/**
 * React hook for combined permissions (Level 1 + Level 2).
 *
 * When folder context is provided, canEdit/canCreate/canDelete auto-select folder-aware checks.
 * When no folder context, they use role-based checks only.
 *
 * @example
 * // Without folder context - uses role-based permissions
 * const { canEdit } = usePermissions();
 * canEdit('screen'); // Role-based only
 *
 * @example
 * // With folder context - auto-selects folder-aware permissions
 * const { canEdit } = usePermissions({ parentFolder });
 * canEdit('screen'); // Role + folder security combined
 *
 * @param context - Optional folder context for folder-level security checks
 */
export function usePermissions(
  context: FolderContext = {},
  options: { ignoreRootLock?: boolean } = {},
) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  const perms = getPermissions(me, company, activeTeamId, context, { isAccountInactive });
  // §12 HOME PATH lock: inside a Root-locked content subtree (path=null, non-admin group member with
  // canEditHomePath=false), ITEM create/edit/delete is disabled to mirror the backend Root gate. This
  // applies ONLY to no-folder-context checks (loose items at Root). FOLDER operations are NOT gated —
  // they are governed by folder/group security (securityGroupIds): callers that check FOLDER permissions
  // pass a folder context OR set `ignoreRootLock` (the folder-action hooks do the latter for their
  // role-only SHOW/HIDE check). Default false (no provider) ⇒ no effect anywhere else.
  const rootLocked = useRootActionLock();
  if (!rootLocked || options.ignoreRootLock || perms.hasFolderContext) return perms;
  return {
    ...perms,
    canCreate: () => false,
    canEdit: () => false,
    canDelete: () => false,
  };
}

/**
 * @deprecated Use usePermissions({ folder }) or usePermissions({ parentFolder }) instead.
 * Alias for usePermissions with folder context.
 */
export function useFolderPermissions(context: FolderContext = {}) {
  return usePermissions(context);
}
